import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setPageSize,
} from "../../../redux/trademark/trademark";
import {
  getListTrademark,
  updateTrademark,
  deleteTrademark,
} from "../../../api/Trademark/Trademark";
import { NavLink } from "react-router-dom";
import {
  SaveOutlined,
  CloseOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Table,
  Spin,
  Form,
  Input,
  message,
  Col,
  Row,
  Switch,
  Card,
} from "antd";
import FormAddTrademark from "./FormAddTrademark";
import { getDisplayNameFromUser } from "../../../utils/userDisplayName";
import { useSession } from "../../../auth/session";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { neutral } from "../../../theme/tokens";

export default function TrademarkTable() {
  const {
    listTrademark,
    isLoadTrademark,
    currentPage,
    pageSize,
    totalRecords,
  } = useSelector((state) => state.persistedReducer.trademarkSlice);
  const { user } = useSession();

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editedRecord, setEditedRecord] = useState({});

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  // Gọi API lấy danh sách khi component mount hoặc khi currentPage/pageSize thay đổi
  useEffect(() => {
    dispatch(getListTrademark({ currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleSizeChange = useCallback(
    (current, size) => {
      setPageSize(size);
    },
    [setPageSize]
  );

  // Kiểm tra xem dòng có đang được chỉnh sửa không
  const isEditing = (record) => record.id === editingKey;

  // Bắt đầu chỉnh sửa
  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
    setEditedRecord(record);
  };

  // Hủy chỉnh sửa
  const cancel = () => {
    setEditingKey("");
  };

  // Lưu dữ liệu đã chỉnh sửa
  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const updatedData = { ...row };
      const response = await dispatch(
        updateTrademark({ id, data: updatedData })
      );
      if (updateTrademark.fulfilled.match(response)) {
        message.success("Sửa thành công");
        await dispatch(getListTrademark({ currentPage, pageSize }));
      } else if (updateTrademark.rejected.match(response)) {
        if (response.payload.message) {
          message.error(response.payload.message);
        }
        if (response.payload.errors) {
          Object.keys(response.payload.errors).forEach((field) => {
            const errorMessages = response.payload.errors[field];
            errorMessages.forEach((errorMessage) => {
              message.error(`${errorMessage}`);
            });
          });
        }
      }
      setEditingKey("");
    } catch (err) {
      console.error("Có lỗi xảy ra:", err);
    }
  };

  const deleteEventRecord = async (id) => {
    try {
      await dispatch(deleteTrademark(id)); // Gọi API xóa
      dispatch(getListTrademark({ currentPage, pageSize })); // Lấy lại danh sách sau khi xóa
    } catch (err) {
      console.error("Error deleting service:", err);
    }
  };

  // Thêm hàm xử lý thay đổi trạng thái
  const handleStatusChange = async (checked, record) => {
    try {
      const updatedData = { status: checked }; // Tạo object mới với trạng thái cập nhật
      const response = await dispatch(
        updateTrademark({ id: record.id, data: updatedData })
      ); // Gọi API cập nhật
      if (updateTrademark.fulfilled.match(response)) {
        message.success("Cập nhật trạng thái thành công!");
        await dispatch(getListTrademark({ currentPage, pageSize })); // Lấy lại danh sách sau khi cập nhật
      } else {
        message.error("Cập nhật trạng thái thất bại!");
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "5%",
    },
    {
      title: "Tài khoản ",
      dataIndex: "auth_user_id",
      key: "auth_user_id",
      width: "10%",
      render: (_, record) =>
        getDisplayNameFromUser(record.user, "Không có dữ liệu"),
    },
    {
      title: "Tên thương hiệu",
      dataIndex: "name",
      key: "name",
      width: "40%",
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: "Tên thương hiệu",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.name
        );
      },
    },
    {
      title: "Liên kết chi nhánh",
      dataIndex: "branch",
      key: "branch",
      width: "20%",
      render: (_, record) => (
        <ul>
          {record.branch?.map((company, index) => (
            <li key={index}>{company.name}</li>
          ))}
        </ul>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: "15%",
      render: (_, record) => (
        <Switch
          checked={record.status} // Giá trị hiện tại
          onChange={(checked) => handleStatusChange(checked, record)} // Hàm xử lý thay đổi
          checkedChildren="Bật"
          unCheckedChildren="Tắt"
        />
      ),
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: "20%",
      render: (_, record) => {
        const editable = isEditing(record); // Kiểm tra nếu dòng đang chỉnh sửa
        return editable ? (
          <>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)} // Lưu dữ liệu
              style={{ marginRight: 8 }}
            >
              Lưu
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel} // Hủy chỉnh sửa
              danger
              style={{ color: neutral[500] }}
            >
              Hủy
            </Button>
          </>
        ) : (
          <RowActions
            disabled={editingKey !== ""}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                onClick: () => edit(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa thương hiệu",
                danger: true,
                divider: true,
                onClick: () =>
                  confirmDelete(record.name || `thương hiệu #${record.id}`, () =>
                    deleteEventRecord(record.id)
                  ),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <div className="bg-gray-50 min-h-screen p-6">
        <Card className="mb-6 shadow-sm">
          <Breadcrumb separator=">">
            <Breadcrumb.Item>Trang chủ</Breadcrumb.Item>
            <Breadcrumb.Item>Thương hiệu SMS</Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="/trademarks">Quản lý thương hiệu SMS</NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>
          {["ADMIN", "SHOP"].includes(user?.data.type) && (
            <Row gutter={[16, 16]}>
              <Col xl lg md sm>
                <Button
                  type="primary"
                  onClick={showModal}
                  icon={<PlusCircleOutlined />}
                >
                  Thêm mới
                </Button>
                <FormAddTrademark
                  isModalOpen={isModalOpen}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  handleOk={handleOk}
                  handleCancel={handleCancel}
                />
              </Col>
            </Row>
          )}
          <Divider />

          <Form form={form} component={false}>
            <Table
              columns={columns}
              loading={listTrademark?.length === 0 ? isLoadTrademark : false}
              dataSource={isLoadTrademark ? [] : listTrademark} // Dữ liệu hiển thị trong bảng
              rowKey="id"
              pagination={{
                showTotal: (totalRecords, range) =>
                  `Hiển thị từ ${range[0]} -> ${range[1]} trong tổng ${totalRecords} giá trị`,
                showQuickJumper: true,
                showSizeChanger: true,
                onShowSizeChange: handleSizeChange,
                total: totalRecords,
                current: currentPage,
                onChange: handlePageChange,
                pageSizeOptions: [10, 20, 30],
              }}
              locale={{
                emptyText: <Empty description="Không có sự kiện!" />,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Form>
        </Card>
      </div>
    </div>
  );
}
